import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, interval } from 'rxjs';

export interface CountdownTick {
  minutes: number;
  seconds: number;
}

export interface CountdownDuration {
  minutes?: number;
  seconds?: number;
}

@Injectable({ providedIn: 'root' })
export class CountdownTimerService {
  private finishAt: number | null = null;
  private tickerSub: Subscription | null = null;

  private tickSubject = new BehaviorSubject<CountdownTick>({ minutes: 0, seconds: 0 });
  private finishedSubject = new Subject<void>();

  /**
   * Emite el estado actual del conteo (cada segundo).
   */
  readonly tick$: Observable<CountdownTick> = this.tickSubject.asObservable();

  /**
   * Emite un evento cuando el conteo llega a 0.
   */
  readonly finished$: Observable<void> = this.finishedSubject.asObservable();

  /**
   * Inicia el conteo regresivo desde una duración específica.
   */
  startWithDuration(duration: CountdownDuration) {
    const totalMs = this.durationToMs(duration);
    const now = Date.now();
    this.startUntil(new Date(now + totalMs));
  }

  /**
   * Inicia el conteo regresivo hasta una fecha/hora objetivo.
   */
  startUntil(finishDate: Date) {
    this.stop();
    this.finishAt = finishDate.getTime();

    // Emitir inmediatamente el primer tick para evitar el salto del primer segundo
    this.updateTick();

    this.tickerSub = interval(1000).subscribe(() => this.updateTick());
  }

  /**
   * Detiene el conteo y limpia recursos.
   */
  stop() {
    this.tickerSub?.unsubscribe();
    this.tickerSub = null;
  }

  private durationToMs({ minutes = 0, seconds = 0 }: CountdownDuration): number {
    const totalSeconds = (minutes * 60) + seconds;
    return totalSeconds * 1000;
  }

  private updateTick() {
    if (this.finishAt == null) return;

    const now = Date.now();
    let diff = this.finishAt - now;

    // Evitar valores negativos que provocan saltos a 59:59
    if (diff <= 0) {
      this.tickSubject.next({ minutes: 0, seconds: 0 });
      this.stop();
      this.finishedSubject.next();
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    this.tickSubject.next({ minutes, seconds });
  }
}
